<script lang="ts" setup>
import type { SlideshowConfig } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { useHead } from "@unhead/vue"
import { computed } from "vue"
import { useRouter } from "vue-router"
import { GET_SLIDESHOW_CONFIG, useGetEventVault, useGetSlideshowConfig, useUpdateSlideshowConfig } from "@/api"
import { useGetSlideshowVideoPlan } from "@/api/getSlideshowVideoPlan"

import { SlideshowForm } from "@/components"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"
import { TaggingBehavior } from "@/types"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/slideshow/[slideshowId]", value => Number(value))
const slideshowId = useConvertedRouteParam("slideshowId", "/event/[id]/slideshow/[slideshowId]")

const { t } = useTranslation()
const toast = useToast()
const router = useRouter()

const { data: slideshowConfig, isLoading: isLoadingSlideshowConfig } = useGetSlideshowConfig(slideshowId, eventVaultId)
const { data: eventVault, isLoading: isLoadingEventVault } = useGetEventVault(eventVaultId)
const { data: slideshowVideoPlan } = useGetSlideshowVideoPlan(eventVaultId)

const queryClient = useQueryClient()

const { mutate } = useUpdateSlideshowConfig()

function onSlideshowSubmit(updatedConfig: SlideshowConfig) {
  if (!eventVaultId.value) {
    return
  }

  mutate(
    { slideshowConfig: updatedConfig, eventVaultId: eventVaultId.value },
    {
      onSuccess: async () => {
        toast.success({
          title: t("success"),
          description: t("host_edit_slideshow.success_content"),
          icon: "CircleCheck",
        })

        await queryClient.invalidateQueries({ queryKey: [GET_SLIDESHOW_CONFIG] })

        await router.push(`/event/${eventVaultId.value}/slideshow`)
      },
      onError: () => toast.error(),
    },
  )
}

const isLoading = computed(() => {
  return Boolean(isLoadingSlideshowConfig.value || isLoadingEventVault.value)
})

useHead({ title: t("host_edit_slideshow.title") })
</script>

<template>
  <SidebarHostEventLayout>
    <h1 class="font-heading font-bold text-2xl mb-8">
      {{ t("host_edit_slideshow.title") }}
    </h1>
    <SlideshowForm
      v-if="!isLoading" :is-loading="isLoading"
      :albums-enabled="eventVault?.eventVaultSettings?.albumsEnabled"
      :tags-enabled="eventVault?.eventVaultSettings?.taggingBehavior === TaggingBehavior.On"
      :has-feature-image="Boolean(eventVault?.coverImageUrl)"
      :slideshow-videos-enabled="slideshowVideoPlan?.slideshowVideosEnabled ?? false"
      :slideshow-config="slideshowConfig" @submit="onSlideshowSubmit"
    />
  </SidebarHostEventLayout>
</template>
